import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Business,
  Home,
  People,
  Work,
  AttachMoney,
  TrendingUp,
  Warning,
  Inventory,
  CheckCircle,
  Cancel,
  Schedule,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatNumber, formatCurrency as formatCurrencyHelper, formatDate } from '../../utils/i18nHelpers';

interface DashboardStats {
  total_buildings: number;
  total_flats: number;
  occupied_flats: number;
  vacant_flats: number;
  vacant_ready_to_rent_flats?: number;
  under_maintenance_flats?: number;
  total_inventory_items: number;
  low_stock_items: number;
  total_inventory_value: number;
  total_work_orders: number;
  pending_work_orders: number;
  in_progress_work_orders: number;
  completed_work_orders: number;
  cancelled_work_orders: number;
  urgent_work_orders: number;
  total_expenses: number;
  monthly_expenses: number;
  weekly_expenses: number;
  total_check_ins: number;
}

interface TrendData {
  week?: string;
  week_start?: string;
  week_end?: string;
  month?: string;
  expenses: number;
  work_orders: number;
}

interface DashboardData {
  filter: string;
  statistics: DashboardStats;
  recent_activities: {
    work_orders: Array<any>;
    expenses: Array<any>;
  };
  buildings: Array<any>;
  trends: {
    weekly: TrendData[];
    monthly: TrendData[];
  };
  breakdowns: {
    work_orders_by_category: Record<string, number>;
    work_orders_by_priority: Record<string, number>;
    expenses_by_category: Record<string, number>;
  };
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [workOrdersPage, setWorkOrdersPage] = useState(0);
  const [expensesPage, setExpensesPage] = useState(0);
  const [trendsPage, setTrendsPage] = useState(0);
  const { t, language } = useLanguage();
  const theme = useTheme();

  const ITEMS_PER_PAGE = 5;
  const TRENDS_PER_PAGE = 4;

  useEffect(() => {
    // Reset pagination when date filter changes
    setWorkOrdersPage(0);
    setExpensesPage(0);
    setTrendsPage(0);
    fetchDashboardData();
  }, [dateFilter]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard', {
        params: { filter: dateFilter },
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!data) {
    return <Typography>{t('dashboard.noData')}</Typography>;
  }

  const stats = data.statistics;

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, fontSize: 48, opacity: 0.8 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const formatCurrency = (amount: number) => {
    return formatCurrencyHelper(amount, language);
  };

  const translateStatus = (status: string): string => {
    const key = status === 'in_progress' ? 'inProgress' : status === 'paused' ? 'paused' : status;
    return t(`workOrders.status.${key}`);
  };

  const translatePriority = (priority: string): string => {
    return t(`workOrders.priority.${priority}`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('dashboard.title')}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('dashboard.dateFilter.label')}</InputLabel>
          <Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            label={t('dashboard.dateFilter.label')}
          >
            <MenuItem value="all">{t('dashboard.dateFilter.all')}</MenuItem>
            <MenuItem value="today">{t('dashboard.dateFilter.today')}</MenuItem>
            <MenuItem value="week">{t('dashboard.dateFilter.week')}</MenuItem>
            <MenuItem value="month">{t('dashboard.dateFilter.month')}</MenuItem>
            <MenuItem value="year">{t('dashboard.dateFilter.year')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={t('dashboard.stats.totalBuildings')}
                value={formatNumber(stats.total_buildings, language)}
                icon={<Business />}
                color={theme.palette.primary.main}
              />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={t('dashboard.stats.totalFlats')}
                value={formatNumber(stats.total_flats, language)}
                icon={<Home />}
                color="#2e7d32"
            subtitle={`${formatNumber(stats.occupied_flats, language)} ${t('dashboard.stats.occupied')}, ${formatNumber(stats.vacant_flats, language)} ${t('dashboard.stats.vacant')}, ${formatNumber(stats.vacant_ready_to_rent_flats || 0, language)} ${t('dashboard.stats.vacantReadyToRent')}, ${formatNumber(stats.under_maintenance_flats || 0, language)} ${t('dashboard.stats.underMaintenance')}`}
              />
        </Grid>
        {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
            title={t('dashboard.stats.inventoryItems')}
            value={stats.total_inventory_items}
            icon={<Inventory />}
                color="#ed6c02"
            subtitle={`${stats.low_stock_items} ${t('dashboard.stats.lowStock')}`}
              />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
            title={t('dashboard.stats.inventoryValue')}
            value={formatCurrency(stats.total_inventory_value)}
            icon={<TrendingUp />}
            color="#7b1fa2"
              />
        </Grid> */}
      </Grid>
          
      {/* Work Orders Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('dashboard.workOrders.sectionTitle')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('dashboard.workOrders.total')}
            value={formatNumber(stats.total_work_orders, language)}
            icon={<Work />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
            title={t('dashboard.workOrders.pending')}
                value={formatNumber(stats.pending_work_orders, language)}
            icon={<Schedule />}
            color="#f57c00"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('dashboard.workOrders.inProgress')}
            value={formatNumber(stats.in_progress_work_orders, language)}
                icon={<Work />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('dashboard.workOrders.completed')}
            value={formatNumber(stats.completed_work_orders, language)}
            icon={<CheckCircle />}
            color="#2e7d32"
              />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
            title={t('dashboard.workOrders.urgent')}
                value={formatNumber(stats.urgent_work_orders, language)}
                icon={<Warning />}
            color="#d32f2f"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('dashboard.workOrders.cancelled')}
            value={formatNumber(stats.cancelled_work_orders, language)}
            icon={<Cancel />}
            color="#616161"
              />
        </Grid>
      </Grid>

      {/* Financial Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('dashboard.financial.sectionTitle')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t('dashboard.financial.totalExpenses')}
            value={formatCurrency(stats.total_expenses)}
            icon={<AttachMoney />}
            color="#7b1fa2"
            subtitle={dateFilter !== 'all' ? t('dashboard.financial.filtered') : t('dashboard.financial.allTime')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <StatCard
                title={t('dashboard.financial.monthlyExpenses')}
            value={formatCurrency(stats.monthly_expenses)}
            icon={<TrendingUp />}
                color="#388e3c"
            subtitle={t('dashboard.financial.thisMonth')}
              />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <StatCard
            title={t('dashboard.financial.weeklyExpenses')}
            value={formatCurrency(stats.weekly_expenses)}
            icon={<AttachMoney />}
            color="#ed6c02"
            subtitle={t('dashboard.financial.thisWeek')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Work Orders by Category */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.breakdowns.byCategory')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(data.breakdowns.work_orders_by_category || {}).map(([category, count]) => (
                  <Chip
                    key={category}
                    label={`${category}: ${formatNumber(count as number, language)}`}
                    color="primary"
                    variant="outlined"
                    sx={{ borderColor: 'divider' }}
                  />
                ))}
            </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Work Orders by Priority */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.breakdowns.byPriority')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(data.breakdowns.work_orders_by_priority || {}).map(([priority, count]) => (
                  <Chip
                    key={priority}
                    label={`${translatePriority(priority)}: ${formatNumber(count as number, language)}`}
                    color={priority === 'urgent' ? 'error' : priority === 'high' ? 'warning' : 'default'}
                    variant="outlined"
                    sx={{ borderColor: 'divider' }}
                  />
                ))}
          </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Expenses by Category */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.breakdowns.expensesByCategory')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(data.breakdowns.expenses_by_category || {}).map(([category, total]) => (
                  <Chip
                    key={category}
                    label={`${category}: ${formatCurrency(total)}`}
                    color="success"
                    variant="outlined"
                    sx={{ borderColor: 'divider' }}
                  />
                ))}
        </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activities */}
      <Grid container spacing={2}>
        {/* Recent Work Orders */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">
                  {t('dashboard.recent.workOrders')}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => setWorkOrdersPage(Math.max(0, workOrdersPage - 1))}
                    disabled={workOrdersPage === 0}
                  >
                    <KeyboardArrowUp />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setWorkOrdersPage(workOrdersPage + 1)}
                    disabled={(workOrdersPage + 1) * ITEMS_PER_PAGE >= data.recent_activities.work_orders.length}
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('dashboard.recent.workOrders.title')}</TableCell>
                      <TableCell>{t('dashboard.recent.workOrders.status')}</TableCell>
                      <TableCell>{t('dashboard.recent.workOrders.priority')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recent_activities.work_orders
                      .slice(workOrdersPage * ITEMS_PER_PAGE, (workOrdersPage + 1) * ITEMS_PER_PAGE)
                      .map((wo) => (
                      <TableRow key={wo.id}>
                        <TableCell>{wo.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={translateStatus(wo.status)}
                            size="small"
                            color={
                              wo.status === 'completed' ? 'success' :
                              wo.status === 'pending' ? 'warning' :
                              wo.status === 'in_progress' ? 'info' : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={translatePriority(wo.priority)}
                            size="small"
                            color={wo.priority === 'urgent' ? 'error' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Expenses */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">
                  {t('dashboard.recent.expenses')}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => setExpensesPage(Math.max(0, expensesPage - 1))}
                    disabled={expensesPage === 0}
                  >
                    <KeyboardArrowUp />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setExpensesPage(expensesPage + 1)}
                    disabled={(expensesPage + 1) * ITEMS_PER_PAGE >= data.recent_activities.expenses.length}
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('dashboard.recent.expenses.description')}</TableCell>
                      <TableCell>{t('dashboard.recent.expenses.amount')}</TableCell>
                      <TableCell>{t('dashboard.recent.expenses.date')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recent_activities.expenses
                      .slice(expensesPage * ITEMS_PER_PAGE, (expensesPage + 1) * ITEMS_PER_PAGE)
                      .map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell>{expense.notes || expense.vendor || expense.service_type || expense.expense_number || '-'}</TableCell>
                        <TableCell>{formatCurrency(expense.amount)}</TableCell>
                        <TableCell>
                          {formatDate(expense.expense_date, language)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Weekly Trends */}
      {data.trends.weekly && data.trends.weekly.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                {t('dashboard.trends.weekly.sectionTitle')}
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => setTrendsPage(Math.max(0, trendsPage - 1))}
                  disabled={trendsPage === 0}
                >
                  <KeyboardArrowUp />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setTrendsPage(trendsPage + 1)}
                  disabled={(trendsPage + 1) * TRENDS_PER_PAGE >= data.trends.weekly.length}
                >
                  <KeyboardArrowDown />
                </IconButton>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#aaa' }}>{t('dashboard.trends.weekly.week')}</TableCell>
                    <TableCell sx={{ color: '#aaa' }} align="right">{t('dashboard.trends.weekly.expenses')}</TableCell>
                    <TableCell sx={{ color: '#aaa' }} align="right">{t('dashboard.trends.weekly.workOrders')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.trends.weekly
                    .slice(trendsPage * TRENDS_PER_PAGE, (trendsPage + 1) * TRENDS_PER_PAGE)
                    .map((trend, idx) => {
                    // Format week range with proper localization
                    let weekLabel = '';
                    if (trend.week_start && trend.week_end) {
                      const weekStart = formatDate(trend.week_start, language, { month: 'short', day: 'numeric' });
                      const weekEnd = formatDate(trend.week_end, language, { month: 'short', day: 'numeric' });
                      weekLabel = `${weekStart} - ${weekEnd}`;
                    } else if (trend.week) {
                      // Fallback to old format if backend hasn't been updated
                      weekLabel = trend.week;
                    }
                    return (
                      <TableRow key={idx}>
                        <TableCell>{weekLabel}</TableCell>
                        <TableCell align="right">{formatCurrency(trend.expenses)}</TableCell>
                        <TableCell align="right">{formatNumber(trend.work_orders, language)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Dashboard;
